import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import styles from './Auth.module.css'

type MessageType = 'success' | 'error' | ''

interface Message {
    type: MessageType
    text: string
}

export function Auth() {
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [isSignUp, setIsSignUp] = useState(false)
    const [message, setMessage] = useState<Message>({ type: '', text: '' })

    const showMessage = (type: Exclude<MessageType, ''>, text: string) => {
        setMessage({ type, text })
        setTimeout(() => setMessage({ type: '', text: '' }), 5000)
    }

    const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault()
        setLoading(true)
        setMessage({ type: '', text: '' })

        try {
            if (isSignUp) {
                const { error } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                        emailRedirectTo: globalThis.location.origin
                    }
                })
                if (error) throw error
                showMessage('success', t('auth.successSignup'))
            } else {
                const { error } = await supabase.auth.signInWithPassword({ email, password })
                if (error) throw error
            }
        } catch (error) {
            console.error('Auth error caught:', error)
            showMessage('error', (error as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const submitLabel = isSignUp ? t('auth.signup') : t('auth.login')

    return (
        <div className={styles.authContainer} data-testid="auth-container">
            <div className={styles.authBackground}>
                <div className={`${styles.authBlob} ${styles.authBlob1}`}></div>
                <div className={`${styles.authBlob} ${styles.authBlob2}`}></div>
                <div className={`${styles.authBlob} ${styles.authBlob3}`}></div>
            </div>

            <div className={`${styles.authCard} animate-fade-in-up`}>
                <div className={`${styles.authLogo} animate-float`}>
                    <span className={styles.authLogoIcon}>📚</span>
                </div>



                <h1 className={styles.authTitle} data-testid="auth-title">{t('auth.title')}</h1>
                <p className={styles.authSubtitle} data-testid="auth-subtitle">{t('auth.subtitle')}</p>

                <form onSubmit={handleSubmit} className={styles.authForm}>
                    <div className={styles.inputGroup}>
                        <input
                            id="email"
                            data-testid="email-input"
                            type="email"
                            placeholder=" "
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                        />
                        <label htmlFor="email">{t('auth.email')}</label>
                        <span className={styles.inputIcon}>✉️</span>
                    </div>

                    <div className={styles.inputGroup}>
                        <input
                            id="password"
                            data-testid="password-input"
                            type="password"
                            placeholder=" "
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete={isSignUp ? 'new-password' : 'current-password'}
                        />
                        <label htmlFor="password">{t('auth.password')}</label>
                        <span className={styles.inputIcon}>🔒</span>
                    </div>

                    {message.text && (
                        <div className={`${styles.authMessage} ${message.type ? styles[message.type] : ''} animate-fade-in`} data-testid={`auth-message-${message.type}`}>
                            <span className={styles.messageIcon}>{message.type === 'success' ? '✓' : '⚠'}</span>
                            {message.text}
                        </div>
                    )}

                    <button type="submit" className={`${styles.authSubmit} btn-primary`} disabled={loading} data-testid="submit-login-button">
                        {loading ? (
                            <>
                                <span className={styles.spinner}></span> {t('auth.loading')}
                            </>
                        ) : (
                            submitLabel
                        )}
                    </button>
                </form>

                <div className={styles.authDivider}>
                    <span>o</span>
                </div>

                <button type="button" className={`${styles.authToggle} btn-secondary`} onClick={() => setIsSignUp(!isSignUp)} data-testid="auth-toggle">
                    {isSignUp ? t('auth.toggleLogin') : t('auth.toggleSignup')}
                </button>

                <p className={styles.authFooter} data-testid="auth-footer">{t('auth.footer')}</p>
            </div>
        </div>
    )
}
